// Neo's receipt for the owner's own panel looks (TOM-595, cloud ADR 0364): a
// card under the answer, beside the board receipt when the request changed a
// board too, saying plainly that only this owner sees the change, with one Undo
// for it. Built from the turn's `looks` records, never from Neo's words. When
// the request changed a board too, its Undo is the whole request's, shared
// with the board receipt (useWholeTurnUndos), so it never undoes only half.

import { useEffect, useRef, useState } from 'react';

import { announceLooksChanged } from '../lib/boardLooks';
import { NEO_BOARD_UPDATED_EVENT } from '../lib/neoBoard';
import {
  LOOKS_SUPERSEDED_TEXT,
  looksOutcomeText,
  looksReceiptScope,
  looksReceiptStep,
  looksTurnOutcome,
  undoLooksPart,
  type LooksReceiptPart,
  type LooksUndoOutcome,
} from '../lib/neoLooksReceipt';
import type { ReceiptLineKind } from '../lib/neoReceipt';
import type { ReceiptStep } from '../lib/receiptStep';
import { useBrowserOnline, type WholeTurnUndo } from './NeoReceipt';
import { Icon } from './ui/Icon';

const LINE_ICON: Readonly<Record<ReceiptLineKind, string>> = {
  added: 'plus',
  changed: 'pencil',
  removed: 'minus',
  moved: 'move',
  resized: 'expand',
  plain: 'check',
};

export function NeoLooksReceipt({
  parts,
  boatName,
  superseded,
  wholeTurn,
}: {
  parts: readonly LooksReceiptPart[];
  /** The boat's display name, for the scope line. */
  boatName: (boatId: string) => string | null;
  /** Keys of the parts a later request changed again: they offer no Undo. */
  superseded?: ReadonlySet<string>;
  /**
   * Set when the request changed a board too (TOM-595): Undo undoes the whole
   * request, and every part reads its outcome from the answer.
   */
  wholeTurn?: WholeTurnUndo;
}) {
  const online = useBrowserOnline();
  const [ownOutcomes, setOutcomes] = useState<Readonly<Record<string, LooksUndoOutcome>>>({});
  const [ownUndoing, setUndoing] = useState(false);
  const turnAnswer = wholeTurn?.answer ?? null;
  const outcomes: Readonly<Record<string, LooksUndoOutcome>> =
    turnAnswer === null
      ? ownOutcomes
      : {
          ...ownOutcomes,
          ...Object.fromEntries(parts.map((p) => [p.key, looksTurnOutcome(p, turnAnswer)])),
        };
  const undoing = ownUndoing || wholeTurn?.undoing === true;
  // One key per part, kept across Try again, so a retry replays an undo whose
  // answer was lost instead of being refused as changed.
  const keys = useRef(new Map<string, string>());

  // Each part's step, asked of the looks route when the receipt is drawn, as
  // the board receipt does (TOM-381): drawn again after a reload, it doesn't
  // remember an Undo already done. Undo waits, disabled, for the answer.
  const [steps, setSteps] = useState<Readonly<Record<string, ReceiptStep>> | null>(null);
  const toAsk = parts.filter((p) => superseded?.has(p.key) !== true);
  const askKey = toAsk.map((p) => `${p.key}@${p.revision}`).join('|');
  const askRef = useRef(toAsk);
  askRef.current = toAsk;
  useEffect(() => {
    let live = true;
    void Promise.all(
      askRef.current.map(async (part) => [part.key, await looksReceiptStep(part)] as const)
    ).then((results) => {
      if (live) setSteps(Object.fromEntries(results));
    });
    return () => {
      live = false;
    };
  }, [askKey]);
  const checking = steps === null && toAsk.length > 0;

  const isSuperseded = (part: LooksReceiptPart) =>
    superseded?.has(part.key) === true && outcomes[part.key] === undefined;
  const isGone = (part: LooksReceiptPart) =>
    outcomes[part.key] === undefined && steps?.[part.key] === 'gone';
  const undoable = parts.filter((p) => !isSuperseded(p) && !isGone(p));
  const pending = undoable.filter((p) => {
    const outcome = outcomes[p.key];
    return outcome === undefined || outcome.kind === 'failed';
  });
  const failed = undoable.some((p) => outcomes[p.key]?.kind === 'failed');

  async function undo() {
    if (undoing || pending.length === 0) return;
    if (wholeTurn !== undefined) {
      wholeTurn.undo();
      return;
    }
    setUndoing(true);
    const results = await Promise.all(
      pending.map(async (part) => {
        let key = keys.current.get(part.key);
        if (key === undefined) {
          key = crypto.randomUUID();
          keys.current.set(part.key, key);
        }
        return [part, await undoLooksPart(part, key)] as const;
      })
    );
    const next: Record<string, LooksUndoOutcome> = {};
    let changed = false;
    for (const [part, outcome] of results) {
      next[part.key] = outcome;
      if (outcome.kind === 'undone') {
        changed = true;
        announceLooksChanged(part.boatId);
      }
    }
    // An open board draws the owner's looks: it reads itself again.
    if (changed) window.dispatchEvent(new CustomEvent(NEO_BOARD_UPDATED_EVENT));
    setOutcomes((prev) => ({ ...prev, ...next }));
    setUndoing(false);
  }

  return (
    <section
      className="neo-receipt neo-receipt--private"
      aria-label="What Neo changed for you only"
      data-testid="neo-looks-receipt"
    >
      {parts.map((part, i) => {
        const outcome = outcomes[part.key];
        return (
          <div
            key={part.key}
            className={`neo-receipt__part${i > 0 ? ' neo-receipt__part--split' : ''}`}
          >
            <div className="neo-receipt__head">
              <span className="neo-receipt__tile neo-receipt__tile--mine" aria-hidden="true">
                <Icon name="user" size={16} />
              </span>
              <span className="neo-receipt__titles">
                <b className="neo-receipt__heading">{part.heading}</b>
                <small className="neo-receipt__scope neo-receipt__scope--mine">
                  {looksReceiptScope(boatName(part.boatId))}
                </small>
              </span>
            </div>
            {part.lines.length > 0 ? (
              <ul className="neo-receipt__lines">
                {part.lines.map((line) => (
                  <li
                    key={line.key}
                    className={`neo-receipt__line neo-receipt__line--${line.kind}`}
                  >
                    <Icon name={LINE_ICON[line.kind]} size={16} />
                    <span>{line.text}</span>
                  </li>
                ))}
              </ul>
            ) : null}
            {outcome !== undefined && outcome.kind !== 'failed' ? (
              <p className="neo-receipt__note" role="status">
                {looksOutcomeText(outcome)}
              </p>
            ) : isSuperseded(part) ? (
              <p className="neo-receipt__note">{LOOKS_SUPERSEDED_TEXT}</p>
            ) : isGone(part) ? (
              <p className="neo-receipt__note">
                {looksOutcomeText({ kind: 'refused', reason: 'nothing' })}
              </p>
            ) : null}
          </div>
        );
      })}
      {failed && !undoing ? (
        <p className="neo-receipt__note" role="alert">
          {looksOutcomeText({ kind: 'failed' })}
        </p>
      ) : null}
      {pending.length > 0 && !online ? (
        <p className="neo-receipt__note">You’re offline. This needs a connection.</p>
      ) : null}
      {pending.length > 0 ? (
        <div className="neo-receipt__actions">
          <button
            type="button"
            className="tb-btn tb-btn--gray tb-btn--sm neo-receipt__button"
            onClick={() => void undo()}
            disabled={undoing || checking || !online}
          >
            {failed ? null : <Icon name="undo" size={15} />}
            {undoing ? 'Undoing…' : failed ? 'Try again' : 'Undo'}
          </button>
        </div>
      ) : null}
    </section>
  );
}
